import React, { useEffect, useState } from "react";
import { 
  Eraser, 
  Maximize, 
  Layout, 
  Wand2, 
  History,
  Download, 
  HelpCircle, 
  CreditCard,
  Sparkles,
  ArrowRight
} from "lucide-react"
import { useAuth } from "../../../AuthProvider";
import { ResultPop } from "../../ResultPop";
import { Usage } from "./Usage";
import { Header } from '../../Header'
import { getRecentActivities } from "../../../api/apiClient";

export const RecentActivities = () => {
  const { currentUser } = useAuth();
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  const handleDownload = async (imageUrl) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = blobUrl;

      // Set the download attribute with a proper filename
      const filename = `gomens-${Date.now()}.${
        blob.type.split("/")[1] || "png"
      }`;
      link.download = filename;

      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the blob URL to free up memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
      // Fallback to the original method if the fetch fails
      const fallbackLink = document.createElement("a");
      fallbackLink.href = imageUrl;
      fallbackLink.download = `gomens-${Date.now()}.png`;
      fallbackLink.target = "_blank"; // Open in new tab if download fails
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
    }
  };

    const fetchRecentActivities = async () => {
    try {
      if (!currentUser) return;

      const headers = {
        Authorization: `Bearer ${await currentUser.getIdToken()}`,
        "X-User-Id": currentUser.uid,
      };

      const response = await getRecentActivities(headers);

      // Ensure response.data is an array
      const activities = Array.isArray(response.data) ? response.data : [];
      setRecentActivities(activities);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching recent activities:", err);
      setError("Failed to load recent activities");
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRecentActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Function to map API response to activity type
  const getActivityDetails = (activity) => {
    const activityTypes = {
      "bg-remove": {
        icon: Eraser,
        color: "text-purple-500",
        description: "Background removed",
      },
      upscale: {
        icon: Maximize,
        color: "text-blue-500",
        description: "Image upscaled",
      },
      expand: {
        icon: Layout,
        color: "text-green-500",
        description: "Image expanded",
      },
      process: {
        icon: Wand2,
        color: "text-orange-500",
        description: "Enhanced image quality",
      },
    };

    return (
      activityTypes[activity.type] || {
        icon: History,
        color: "text-gray-500",
        description: "Image processed",
      }
    );
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      hour12: true,
    };
    return date.toLocaleString(undefined, options);
  };

  return (
    <>
    <Header title={"Recent Activities"}/>
    <div className="min-h-screen bg-[#ffffff] p-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Sidebar */}
        <div className="space-y-8">
          {/* Your Usage Section */}
          <Usage />          
          <div>
        </div>
      </div>

        {/* Right Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Recent Activity */}
          <div>          
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                    </div>
                  ) : error ? (
                    <div className="text-center py-8 text-red-500">{error}</div>
                  ) : recentActivities.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
                        <Sparkles className="w-8 h-8 text-purple-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">No generations yet</h3>
                      <p className="text-gray-500 mb-6 max-w-sm">
                        You haven't generated any images yet. Try the AI Model tool — it takes less than 2 minutes.
                      </p>
                      <a href="/virtualaimodel" className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors">
                        Generate Your First Image
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="divide-y divide-gray-200">
                        {Array.isArray(recentActivities) && recentActivities.map((activity, index) => {
                          const details = getActivityDetails(activity);
                          return (
                            <div
                              key={index}
                              className="p-3 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row items-start sm:items-center"
                            >
                              <div className="flex items-center w-full sm:w-auto">
                                {activity.output_image && (
                                  <img
                                    src={activity.output_image}
                                    alt="Output"
                                    className="w-12 h-12 object-cover rounded-md cursor-pointer mr-4"
                                    onClick={() => setSelectedResult(activity)}
                                  />
                                )}
                                <div className="flex-1 sm:flex-none">
                                  <p className="font-medium text-gray-800">
                                    {activity.type || "Result"}
                                  </p>
                                  <p className="text-sm text-gray-600 block sm:hidden">
                                    {formatDateTime(activity.timestamp)}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1 sm:mt-0">
                                    {details.description}
                                    {activity.details && ` - ${activity.details}`}
                                  </p>
                                </div>
                              </div>

                              <div className="flex-1 hidden sm:block text-right">
                                <span className="text-sm text-gray-500">
                                  {formatDateTime(activity.timestamp)}
                                </span>
                              </div>

                              <div className="mt-3 sm:mt-0 sm:ml-4 flex-shrink-0">
                                <button
                                  onClick={() =>
                                    handleDownload(activity.output_image)
                                  }
                                  className="text-gray-500 hover:text-blue-600"
                                  aria-label="Download image"
                                >
                                  <Download size={18} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <ResultPop
                    data={selectedResult}
                    onClose={() => setSelectedResult(null)}
                  />
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
